import {
  IonBadge,
  IonContent,
  IonInfiniteScroll,
  IonLoading,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRouterLink,
  IonSearchbar,
  NavContext
} from '@ionic/react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import React from 'react';

import { Empty, Padding, Spacer } from '@/components/common';
import {
  DashboardHeader,
  EditProceduresModal,
  RequestItem,
  RequestPlaceholder,
  Reschedule,
  SharePrompt,
  StatusOptions
} from '@/components/dashboard';
import { Icon } from '@/components/memo';
import {
  useClinic,
  useDentist,
  useRequests,
  useSubscription
} from '@/contexts';
import { Request } from '@/data/Requests';
import { usePaywall, useToast } from '@/hooks';
import { Paywall } from '@/pages';
import {
  ENTRIES_PER_PAGE,
  OFF_DUTY_PATIENT_ID,
  REFRESH_TIMEOUT_IN_MS,
  REQUEST_STATUS_ICONS,
  REQUEST_STATUS_OPTIONS
} from '@/utils/constants';
import capitalize from '@/utils/helpers/capitalize';
import { now } from '@/utils/helpers/now';
import { parseDate } from '@/utils/helpers/parseDate';
import { parseTime } from '@/utils/helpers/parseTime';
import { ROUTES } from '@/utils/routes';
import { TABS } from '@/utils/tabs';
import { ensureMinimumDelay } from '@/utils/timing';
import {
  addDays,
  differenceInMinutes,
  format,
  isSameDay,
  isToday,
  parse
} from 'date-fns';
import { refreshOutline, swapHorizontalOutline } from 'ionicons/icons';
import { useLocation } from 'react-router-dom';

import './Requests.css';

interface RequestsProps {}

const Requests: React.FC<RequestsProps> = () => {
  const { isClinic } = useClinic();
  const { onSwitch, dentists } = useDentist();
  const { purchasing } = useSubscription();
  const { showPaywall, onOpenPaywall, onClosePaywall } = usePaywall();
  const {
    selectedStatus,
    setSelectedStatus,
    pendingRequestsCount,
    filteredRequests,
    searchQuery,
    setSearchQuery,
    searchedRequests,
    setFocusedRequestId,
    updating,
    fetchRequestsByStatus,
    isStatusLoaded,
    isStatusLoading,
    isStatusFailed
  } = useRequests();
  const displayToast = useToast();
  const { navigate } = useContext(NavContext);

  const { search } = useLocation();
  const queryFromUrl = new URLSearchParams(search).get('query');
  const statusFromUrl = new URLSearchParams(search).get('status');
  const focusedRequestIdFromUrl = new URLSearchParams(search).get(
    'focusedRequestId'
  );

  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [requestToReschedule, setRequestToReschedule] =
    useState<Request | null>(null);
  const [requestToEditProcedures, setRequestToEditProcedures] =
    useState<Request | null>(null);

  const pageRef = useRef<HTMLElement | undefined>(undefined);
  const contentRef = useRef<HTMLIonContentElement | null>(null);

  const statusOptions = Object.values(REQUEST_STATUS_OPTIONS);

  const filteredAndSearchedRequests = useMemo(() => {
    const requests = searchQuery ? searchedRequests : filteredRequests;
    return requests.filter(
      (request) => request.patient_id !== OFF_DUTY_PATIENT_ID
    );
  }, [searchQuery, searchedRequests, filteredRequests]);

  const groupRequests = (requests: Request[]) => {
    return requests.reduce(
      (acc: Record<string, Request[]>, request: Request) => {
        const requestDate = format(
          request.date ? parseDate(request.date) : now(),
          'MMMM d, yyyy'
        );
        if (!acc[requestDate]) {
          acc[requestDate] = [];
        }
        acc[requestDate].push(request);
        acc[requestDate].sort((a, b) =>
          differenceInMinutes(parseTime(a.start_time), parseTime(b.start_time))
        );
        return acc;
      },
      {}
    );
  };

  const groupedRequests = useMemo(
    () => groupRequests(filteredAndSearchedRequests),
    [filteredAndSearchedRequests]
  );

  const onRefresh = useCallback(
    async (event: CustomEvent) => {
      setTimeout(async () => {
        await fetchRequestsByStatus(selectedStatus);
        event.detail.complete();
      }, REFRESH_TIMEOUT_IN_MS);
    },
    [fetchRequestsByStatus, selectedStatus]
  );

  const onSearch = (event: CustomEvent) => {
    setSearchQuery(event.detail.value);
  };

  const loadMore = async (event: CustomEvent<void>) => {
    const startTime = Date.now();

    setPage((prevPage) => prevPage + 1);
    const infiniteScroll = event.target as HTMLIonInfiniteScrollElement;
    await ensureMinimumDelay(startTime);

    await infiniteScroll.complete();
  };

  const onCloseReschedule = () => {
    setRequestToReschedule(null);
  };

  const onStatusChange = useCallback(
    async (status: string) => {
      if (
        isStatusLoading(status) ||
        isStatusLoaded(status) ||
        isStatusFailed(status)
      ) {
        return;
      }

      try {
        await fetchRequestsByStatus(status);
      } catch {
        displayToast({
          message: `Please check your internet connection.`,
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    },
    [setSelectedStatus, fetchRequestsByStatus, isStatusLoaded, isStatusLoading, isStatusFailed]
  );

  /* Load the requests for the selected status when the status changes */
  useEffect(() => {
    onStatusChange(selectedStatus);
  }, [selectedStatus, onStatusChange]);

  useEffect(() => {
    if (queryFromUrl) {
      setSearchQuery(decodeURIComponent(queryFromUrl));
    }
  }, [queryFromUrl]);

  useEffect(() => {
    if (
      statusFromUrl &&
      Object.values(REQUEST_STATUS_OPTIONS).includes(statusFromUrl)
    ) {
      setSelectedStatus(statusFromUrl);
    }
  }, [statusFromUrl, setSelectedStatus]);

  useEffect(() => {
    const entries =
      selectedStatus === REQUEST_STATUS_OPTIONS.PENDING
        ? Object.entries(groupedRequests)
        : Object.entries(groupedRequests).reverse();

    setHasMore(page * ENTRIES_PER_PAGE < entries.length);
  }, [selectedStatus, searchQuery, groupedRequests, page]);

  useEffect(() => {
    if (
      searchQuery === null ||
      (searchQuery === '' && (queryFromUrl !== null || queryFromUrl !== ''))
    ) {
      navigate(`${ROUTES.REQUESTS}`);
    }
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
    if (contentRef.current) {
      contentRef.current.scrollToTop(0);
    }
  }, [selectedStatus, searchQuery, contentRef.current]);

  useEffect(() => {
    if (focusedRequestIdFromUrl) {
      setFocusedRequestId(focusedRequestIdFromUrl);
    } else {
      setFocusedRequestId(undefined);
    }
  }, [focusedRequestIdFromUrl, setFocusedRequestId]);

  const renderRequestItemsSkeleton = (count: number = 3) => {
    return (
      <div className="requests__date-group">
        {Array.from({ length: count }).map((_, index) => (
          <RequestPlaceholder key={index} />
        ))}
      </div>
    );
  };

  const renderRequestItems = (requests: Request[]) => {
    if (
      isStatusLoading(selectedStatus) ||
      (!isStatusLoaded(selectedStatus) && !isStatusFailed(selectedStatus))
    ) {
      return renderRequestItemsSkeleton();
    }

    if (requests.length === 0) {
      return (
        <>
          <Empty
            icon={REQUEST_STATUS_ICONS[selectedStatus]}
            message={
              searchQuery && searchQuery !== ''
                ? `No results found for "${searchQuery}" on ${capitalize(selectedStatus)}`
                : `No ${selectedStatus} requests found`
            }
            marginTop={72}
          />
          <SharePrompt />
          {dentists.length > 1 && (
            <Spacer top={-32} bottom={16}>
              <div className="requests__switch">
                <IonRouterLink onClick={onSwitch}>
                  <Icon icon={swapHorizontalOutline} />
                  Switch {isClinic ? 'Dentist' : 'Clinic'}
                </IonRouterLink>
              </div>
            </Spacer>
          )}
        </>
      );
    }

    const entries =
      selectedStatus === REQUEST_STATUS_OPTIONS.PENDING
        ? Object.entries(groupedRequests)
        : Object.entries(groupedRequests).reverse();

    const paginatedEntries = entries.slice(0, page * ENTRIES_PER_PAGE);

    return paginatedEntries.map(([date, items]) => (
      <div key={date} className="requests__date-group">
        <div className="requests__date-group-header">
          <h4 className="requests__date-group-label">{date.split(',')[0]}</h4>
          {isToday(parse(date, 'MMMM d, yyyy', now())) && (
            <IonBadge color="primary" className="requests__date-group-badge">
              Today
            </IonBadge>
          )}
          {isSameDay(parse(date, 'MMMM d, yyyy', now()), addDays(now(), 1)) && (
            <IonBadge color="medium" className="requests__date-group-badge">
              Tomorrow
            </IonBadge>
          )}
        </div>
        {items.map((request: Request) => (
          <RequestItem
            key={request.id}
            request={request}
            setRequestToReschedule={setRequestToReschedule}
            onOpenPaywall={onOpenPaywall}
            onEditProcedures={setRequestToEditProcedures}
          />
        ))}
      </div>
    ));
  };

  return (
    <IonPage id="requests" ref={pageRef}>
      <IonContent ref={contentRef}>
        <IonRefresher
          id="requests-refresher"
          slot="fixed"
          onIonRefresh={onRefresh}
        >
          <IonRefresherContent
            pullingIcon={refreshOutline}
            refreshingSpinner="crescent"
          />
        </IonRefresher>

        <Padding>
          <div className="requests__fixed-header">
            <DashboardHeader title="Requests" tab={TABS.REQUESTS} />
            <IonSearchbar
              id="searchbar"
              placeholder="Search requests"
              value={searchQuery}
              onIonInput={onSearch}
              debounce={324}
              onIonClear={() => setSearchQuery('')}
            />
            <Spacer top={4} bottom={16}>
              <StatusOptions
                selectedStatus={selectedStatus}
                onStatusChange={setSelectedStatus}
                statusOptions={statusOptions}
                pendingRequestsCount={pendingRequestsCount}
              />
            </Spacer>
          </div>
          <Spacer top={184} bottom={24}>
            <div className="requests__list">
              {renderRequestItems(filteredAndSearchedRequests)}
            </div>
            {filteredAndSearchedRequests.length > 0 &&
              filteredAndSearchedRequests.length > ENTRIES_PER_PAGE && (
                <IonInfiniteScroll onIonInfinite={loadMore} disabled={!hasMore}>
                  <Spacer top={16}>
                    <div className="requests__list">
                      {renderRequestItemsSkeleton(2)}
                    </div>
                  </Spacer>
                </IonInfiniteScroll>
              )}
          </Spacer>
        </Padding>

        <Reschedule
          isOpen={!!requestToReschedule}
          onClose={onCloseReschedule}
          requestToReschedule={requestToReschedule}
          // TODO: presentingElement is temporarily set to undefined due to an issue with Ionic modal card state when used with IonTabs.
          // Uncomment the line below and remove presentingElement={undefined} once the issue is resolved.
          // presentingElement={page.current}
          presentingElement={undefined}
        />

        <EditProceduresModal
          isOpen={!!requestToEditProcedures}
          onClose={() => setRequestToEditProcedures(null)}
          request={requestToEditProcedures}
          presentingElement={undefined}
        />

        <Paywall
          isOpen={showPaywall}
          onClose={onClosePaywall}
          loading={purchasing}
        />

        <IonLoading isOpen={updating} message="Loading..." spinner="crescent" />
      </IonContent>
    </IonPage>
  );
};

export default Requests;
